import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { ClerkProvider, useUser, useAuth as useClerkAuth } from '@clerk/clerk-react';
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { OnboardingProvider } from "./context/OnboardingContext";
import PerformanceMonitor from "./components/PerformanceMonitor";
import ErrorBoundary from "./components/ErrorBoundary";
import { Toaster } from 'sonner';
import { useIsMobile } from "./hooks/useMediaQuery";
import API_BASE_URL from "./config/api";
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { trackNewUserSignup, trackPurchase } from "./utils/metaPixel";
import "./App.css";

// Clerk Publishable Key
const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error("Missing Clerk Publishable Key");
}

// ClerkProvider wrapper - connects Clerk's navigation to React Router for SPA transitions
// This prevents full page reloads during auth flow (email -> password -> MFA)
// which was causing duplicate MFA emails due to Clerk reinitializing
const ClerkProviderWithNavigation = ({ children, publishableKey }) => {
  const navigate = useNavigate();
  
  return (
    <ClerkProvider 
      publishableKey={publishableKey}
      // Connect Clerk's internal navigation to React Router
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      // Force inline auth components instead of redirecting to Clerk domain
      signInUrl="/auth/login"
      signUpUrl="/auth/register"
      // Use fallbackRedirectUrl (Clerk v5+) instead of deprecated afterSignInUrl/afterSignUpUrl
      signInFallbackRedirectUrl="/dashboard"
      signUpFallbackRedirectUrl="/dashboard"
    >
      {children}
    </ClerkProvider>
  );
};

// Import pages
import HomePage from "./pages/HomePage";
import LandingPage from "./pages/LandingPage";
import HomePageRedesign from "./pages/HomePageRedesign";
import DashboardPage from "./pages/DashboardPage";
import FreeCalculator from "./pages/FreeCalculator";
import Glossary from "./pages/Glossary";
import QuizPage from "./pages/QuizPage";
import SamplePDF from "./pages/SamplePDF";
import Settings from "./pages/Settings";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import MyAccountPage from "./pages/MyAccountPage";
import PricingPage from "./pages/PricingPage";
import SubscriptionSetupPage from "./pages/SubscriptionSetupPage";
import ClerkDebugPage from "./pages/ClerkDebugPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import WelcomePage from "./pages/WelcomePage";
import ToolsPage from "./pages/ToolsPage";
import CommissionSplitCalculator from "./pages/CommissionSplitCalculator";
import SellerNetSheetCalculator from "./pages/SellerNetSheetCalculator";
import AffordabilityCalculator from "./pages/AffordabilityCalculator";
import SetPasswordPage from "./pages/SetPasswordPage";
import SupportPage from "./pages/SupportPage";
import AccountDeletedPage from "./pages/AccountDeletedPage";
import PushRegistrar from "./components/PushRegistrar";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import AccessibilityPage from "./pages/AccessibilityPage";
import FreeToolsPage from "./pages/FreeToolsPage";
import BrokerTeamAnalyzerPage from "./pages/BrokerTeamAnalyzer";
import SellerNetSheetPage from "./pages/SellerNetSheet";
import InvestorDealAnalyzerPage from "./pages/InvestorDealAnalyzer";
import MortgageAffordabilityPage from "./pages/MortgageAffordability";
import PnLPanel from "./components/dashboard/PnLPanel";
import ClosingDateCalculator from "./pages/ClosingDateCalculator";
import AdminConsolePage from "./pages/AdminConsolePage";
import BrandingProfilePage from "./pages/BrandingProfilePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MobileLayout from "./layouts/MobileLayout";
import MobileEntry from "./pages/MobileEntry";
import CompleteSubscriptionPage from "./pages/CompleteSubscriptionPage";
import SubscribePage from "./pages/SubscribePage";

// Blog Pages
import BlogListPage from "./pages/BlogListPage";
import BlogPostPage from "./pages/BlogPostPage";

// Feature Pages
import FeaturesPage from "./pages/FeaturesPage";
import AICoachFeature from "./pages/features/AICoachFeature";
import MortgageCalculatorFeature from "./pages/features/MortgageCalculatorFeature";
import NetSheetFeature from "./pages/features/NetSheetFeature";
import CommissionCalculatorFeature from "./pages/features/CommissionCalculatorFeature";
import ClosingDateFeature from "./pages/features/ClosingDateFeature";
import DealAnalyzerFeature from "./pages/features/DealAnalyzerFeature";
import PnLTrackerFeature from "./pages/features/PnLTrackerFeature";
import CapReportFeature from "./pages/features/CapReportFeature";
import ActionTrackerFeature from "./pages/features/ActionTrackerFeature";
import SocialPostFeature from "./pages/features/SocialPostFeature";

// Note: AdminCommandCenter moved to separate ops-frontend app (ops.ineednumbers.com)

// Import Onboarding Screens
import {
  WelcomeScreen,
  AgentTypeScreen,
  WhyScreen,
  IncomeGoalScreen,
  HomesSoldGoalScreen,
  WeeklyHoursScreen,
  CommissionSetupScreen,
  WeeklyFocusScreen,
  CommissionCapScreen,
  BrandingSetupScreen,
  CompletionScreen,
  SocialVoiceScreen
} from "./screens/onboarding";

/**
 * Dashboard Route Wrapper
 * Conditionally renders mobile or desktop layout based on viewport
 * Also checks subscription status and redirects to complete subscription if needed
 */
function DashboardRoute() {
  const isMobile = useIsMobile();
  const { hasActiveSubscription, loading, user, refreshUser, setUserTrialEnd } = useAuth();
  const { isSignedIn, isLoaded, user: clerkUser } = useUser();
  const { getToken } = useClerkAuth();
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [hasCheckedCheckout, setHasCheckedCheckout] = React.useState(false);
  const [syncComplete, setSyncComplete] = React.useState(false);
  const [needsOnboarding, setNeedsOnboarding] = React.useState(false);
  const [onboardingChecked, setOnboardingChecked] = React.useState(false);

  const backendUrl = API_BASE_URL;

  // STEP 1: Sync user FIRST to ensure trial is assigned
  React.useEffect(() => {
    if (isSignedIn && clerkUser && !syncComplete && isLoaded) {
      const syncUser = async () => {
        try {
          console.log('[DashboardRoute] Syncing user to ensure trial is assigned...');
          const token = await getToken() || '';
          const syncResponse = await fetch(`${backendUrl}/api/clerk/sync-user`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            credentials: 'include',
            body: JSON.stringify({
              clerk_user_id: clerkUser.id,
              email: clerkUser.primaryEmailAddress?.emailAddress,
              full_name: clerkUser.fullName || '',
              metadata: clerkUser.publicMetadata || {}
            })
          });

          // Capture trial_end straight from the sync response — this is the
          // backend-authoritative value and is what unblocks iOS WKWebView when
          // Clerk's publicMetadata snapshot is still empty in the new session.
          try {
            const syncData = await syncResponse.json();
            if (syncData && syncData.trial_end && setUserTrialEnd) {
              console.log('[DashboardRoute] sync-user returned trial_end:', syncData.trial_end);
              setUserTrialEnd(syncData.trial_end);
            }
            // Fire Meta Pixel CompleteRegistration + StartTrial for NEW users only
            if (syncData && syncData.is_new_user === true) {
              console.log('[DashboardRoute] New user detected, firing tracking events');
              trackNewUserSignup();
            }
          } catch (parseErr) {
            console.warn('[DashboardRoute] Could not parse sync-user body:', parseErr);
          }

          // Reload Clerk user to get updated metadata
          await clerkUser.reload();
          // Belt-and-suspenders: give WKWebView one more tick to commit the
          // refreshed publicMetadata before the redirect check runs.
          await new Promise((r) => setTimeout(r, 250));
          console.log('[DashboardRoute] Sync complete, metadata:', clerkUser.publicMetadata);

          if (refreshUser) await refreshUser();
        } catch (err) {
          console.error('[DashboardRoute] Sync error:', err);
        } finally {
          setSyncComplete(true);
        }
      };
      syncUser();
    } else if (!isSignedIn && isLoaded) {
      setSyncComplete(true);
    }
  }, [isSignedIn, clerkUser, syncComplete, isLoaded, refreshUser, backendUrl, getToken, setUserTrialEnd]);

  // STEP 2: ONBOARDING CHECK — runs after sync completes, uses same auth pattern
  React.useEffect(() => {
    if (syncComplete && isSignedIn && clerkUser && !onboardingChecked) {
      const checkOnboarding = async () => {
        try {
          const token = await getToken() || '';
          const response = await fetch(`${backendUrl}/api/onboarding/status`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            credentials: 'include'
          });

          if (response.ok) {
            const data = await response.json();
            console.log('[DashboardRoute] Onboarding status:', data);
            if (!data.onboarding_completed) {
              console.log('[DashboardRoute] First-time user, needs onboarding');
              setNeedsOnboarding(true);
            }
          } else if (response.status === 404) {
            // User not in DB yet — definitely needs onboarding
            console.log('[DashboardRoute] User not found in DB, needs onboarding');
            setNeedsOnboarding(true);
          } else {
            console.warn('[DashboardRoute] Onboarding check failed:', response.status);
            // Don't block dashboard access on error
          }
        } catch (err) {
          console.error('[DashboardRoute] Onboarding check error:', err);
          // Don't block dashboard on network error
        } finally {
          setOnboardingChecked(true);
        }
      };
      checkOnboarding();
    } else if (!isSignedIn && isLoaded) {
      setOnboardingChecked(true);
    }
  }, [syncComplete, isSignedIn, clerkUser, onboardingChecked, backendUrl, getToken]);

  // Check for checkout success (existing logic, unchanged)
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const checkoutStatus = urlParams.get('checkout');
    const sessionId = urlParams.get('session_id');

    if (checkoutStatus === 'success' && !hasCheckedCheckout && clerkUser) {
      setHasCheckedCheckout(true);
      setIsRefreshing(true);

      console.log('[DashboardRoute] Checkout success detected, verifying subscription...');

      const verifyAndRefresh = async () => {
        try {
          const response = await fetch(`${backendUrl}/api/clerk/verify-subscription`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              clerk_user_id: clerkUser.id,
              session_id: sessionId
            }),
            credentials: 'include'
          });

          const result = await response.json();
          console.log('[DashboardRoute] Subscription verification result:', result);

          // Fire Meta Pixel Purchase event if valid session with amount > 0
          if (result && result.verified_session_id && result.amount_charged > 0) {
            console.log('[DashboardRoute] Firing Purchase event:', result.amount_charged, result.currency);
            trackPurchase({
              verified_session_id: result.verified_session_id,
              amount_charged: result.amount_charged,
              currency: result.currency
            });
          }

          await new Promise(resolve => setTimeout(resolve, 1000));
          await clerkUser.reload();

          if (refreshUser) await refreshUser();

          window.history.replaceState({}, '', window.location.pathname);
        } catch (err) {
          console.error('[DashboardRoute] Error verifying subscription:', err);
        } finally {
          setIsRefreshing(false);
        }
      };

      setTimeout(verifyAndRefresh, 1500);
    }
  }, [clerkUser, hasCheckedCheckout, refreshUser, backendUrl]);

  // Wait for auth to load, sync to complete, onboarding check, or refresh to complete.
  // Also hold the spinner if Clerk says we're signed in but the AuthContext user
  // record (which is the only place backend-authoritative trial_end lives) hasn't
  // populated yet — falling through to the redirect check with an empty `user`
  // is what was sending iOS users to /complete-subscription on a valid trial.
  const userNotReady = isSignedIn && syncComplete && onboardingChecked && !user;
  if (loading || !isLoaded || isRefreshing || !syncComplete || !onboardingChecked || userNotReady) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2FA163] mx-auto mb-4"></div>
          {isRefreshing && <p className="text-gray-600">Activating your subscription...</p>}
          {!syncComplete && !isRefreshing && <p className="text-gray-600">Setting up your account...</p>}
          {userNotReady && <p className="text-gray-600">Loading your account…</p>}
        </div>
      </div>
    );
  }

  // If user needs onboarding, redirect BEFORE checking subscription
  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // Check for active trial — read BOTH sources. iOS WKWebView frequently has an
  // empty Clerk publicMetadata snapshot at this point even after `clerkUser.reload()`,
  // so we fall back to the backend-authoritative value pushed onto user.trial_end
  // by the POST /api/clerk/sync-user response above.
  const clerkTrialEnd = clerkUser?.publicMetadata?.trial_end;
  const backendTrialEnd = user?.trial_end;
  const trialEnd = clerkTrialEnd || backendTrialEnd;
  const hasActiveTrial = trialEnd && (trialEnd * 1000) > Date.now();
  
  // Also check if user has PRO plan (from trial or subscription)
  const clerkPlan = clerkUser?.publicMetadata?.plan;
  const userPlan = user?.plan;
  const hasPROPlan = (clerkPlan?.toLowerCase() === 'pro') || (userPlan?.toUpperCase() === 'PRO');
  
  // User can access dashboard if they have active subscription, active trial, OR PRO plan
  const canAccessDashboard = hasActiveSubscription() || hasActiveTrial || hasPROPlan;

  console.log('[DashboardRoute] trial check', {
    clerkTrialEnd,
    backendTrialEnd,
    hasActiveTrial,
    hasActiveSubscription: hasActiveSubscription(),
    clerkPlan,
    userPlan,
    hasPROPlan,
    platform: window.Capacitor?.isNativePlatform?.() ? 'ios' : 'web'
  });

  if (isSignedIn && user && !canAccessDashboard) {
    return <Navigate to="/complete-subscription" replace />;
  }

  if (isMobile) {
    return (
      <ErrorBoundary>
        <MobileLayout>
          <DashboardPage />
        </MobileLayout>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <DashboardPage />
    </ErrorBoundary>
  );
}

/**
 * Calculator Route Wrapper
 * Wraps calculator pages in MobileLayout on mobile to maintain bottom navigation
 */
function CalculatorRoute({ children }) {
  const isMobile = useIsMobile();
  
  if (isMobile) {
    return (
      <ErrorBoundary>
        <MobileLayout>
          {children}
        </MobileLayout>
      </ErrorBoundary>
    );
  }
  
  // On desktop, render calculator directly
  return (
    <ErrorBoundary>
      {children}
    </ErrorBoundary>
  );
}

function App() {
  // Notify Capgo that the app has loaded successfully (for OTA updates)
  React.useEffect(() => {
    CapacitorUpdater.notifyAppReady();
  }, []);

  return (
    <div className="App">
      <HelmetProvider>
        <PerformanceMonitor />
        <Toaster 
          position="top-right" 
          richColors 
          offset="60px"
          toastOptions={{
            style: {
              marginTop: 'env(safe-area-inset-top, 0px)'
            }
          }}
        />
        <BrowserRouter future={{
          v7_relativeSplatPath: true,
          v7_startTransition: true,
          v7_fetcherPersist: true
        }}>
          <ClerkProviderWithNavigation publishableKey={CLERK_PUBLISHABLE_KEY}>
            <AuthProvider>
              <OnboardingProvider>
                <PushRegistrar />
                <Routes>
              {/* Mobile-only entry route - bypasses all layouts */}
              <Route path="/mobile" element={<MobileEntry />} />
              
              {/* New redesigned landing page as main homepage */}
              <Route path="/" element={<HomePageRedesign />} />
              <Route path="/home-legacy" element={<HomePage />} />
              <Route path="/landing-old" element={<LandingPage />} />
              <Route path="/dashboard" element={<DashboardRoute />} />
              <Route path="/calculator" element={<CalculatorRoute><FreeCalculator /></CalculatorRoute>} />
              <Route path="/glossary" element={<Glossary />} />
              <Route path="/sample-pdf" element={<SamplePDF />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/subscription-setup" element={<SubscriptionSetupPage />} />
              <Route path="/complete-subscription" element={<CompleteSubscriptionPage />} />
              <Route path="/subscribe" element={<SubscribePage />} />
              <Route path="/clerk-debug" element={<ClerkDebugPage />} />
              <Route path="/welcome" element={<WelcomePage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/account-deleted" element={<AccountDeletedPage />} />
              
              {/* Feature Pages */}
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/features/ai-coach" element={<AICoachFeature />} />
              <Route path="/features/mortgage-calculator" element={<MortgageCalculatorFeature />} />
              <Route path="/features/net-sheet" element={<NetSheetFeature />} />
              <Route path="/features/commission-calculator" element={<CommissionCalculatorFeature />} />
              <Route path="/features/closing-date" element={<ClosingDateFeature />} />
              <Route path="/features/deal-analyzer" element={<DealAnalyzerFeature />} />
              <Route path="/features/pnl-tracker" element={<PnLTrackerFeature />} />
              <Route path="/features/cap-report" element={<CapReportFeature />} />
              <Route path="/features/action-tracker" element={<ActionTrackerFeature />} />
              <Route path="/features/social-post" element={<SocialPostFeature />} />
              
              {/* Blog Routes */}
              <Route path="/blog" element={<BlogListPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              
              {/* Admin Command Center moved to ops.ineednumbers.com */}
              
              {/* Tools Routes */}
              <Route path="/quiz" element={<QuizPage />} />
              <Route path="/free-tools" element={<FreeToolsPage />} />
              <Route path="/free-tools/broker-team-analyzer" element={<BrokerTeamAnalyzerPage />} />
              <Route path="/free-tools/seller-net-sheet" element={<SellerNetSheetPage />} />
              <Route path="/free-tools/mortgage-affordability" element={<MortgageAffordabilityPage />} />
              <Route path="/free-tools/investor-deal-analyzer" element={<InvestorDealAnalyzerPage />} />
              <Route path="/tools" element={<ToolsPage />} />
              <Route path="/tools/commission-split" element={<CalculatorRoute><CommissionSplitCalculator /></CalculatorRoute>} />
              <Route path="/tools/net-sheet" element={<CalculatorRoute><SellerNetSheetCalculator /></CalculatorRoute>} />
              <Route path="/tools/affordability" element={<CalculatorRoute><AffordabilityCalculator /></CalculatorRoute>} />
              <Route path="/tools/closing-date" element={<CalculatorRoute><ClosingDateCalculator /></CalculatorRoute>} />
              <Route path="/affordability/shared/:calculationId" element={<CalculatorRoute><AffordabilityCalculator /></CalculatorRoute>} />
              <Route path="/tools/agent-pl-tracker" element={<CalculatorRoute><PnLPanel /></CalculatorRoute>} />
              <Route path="/tools/pnl-tracker" element={<CalculatorRoute><PnLPanel /></CalculatorRoute>} />
              {/* Redirect old P&L Tracker URL to correct path */}
              <Route path="/agent-pnl-tracker" element={<Navigate to="/tools/agent-pl-tracker" replace />} />
              <Route path="/login" element={<Navigate to="/auth/login" replace />} />
              <Route path="/app/branding" element={<BrandingProfilePage />} />
              <Route path="/dashboard/branding" element={<BrandingProfilePage />} />
              
              {/* Onboarding Routes */}
              <Route path="/onboarding" element={<WelcomeScreen />} />
              <Route path="/onboarding/welcome" element={<WelcomeScreen />} />
              <Route path="/onboarding/agent-type" element={<AgentTypeScreen />} />
              <Route path="/onboarding/why" element={<WhyScreen />} />
              <Route path="/onboarding/income-goal" element={<IncomeGoalScreen />} />
              <Route path="/onboarding/homes-sold-goal" element={<HomesSoldGoalScreen />} />
              <Route path="/onboarding/weekly-hours" element={<WeeklyHoursScreen />} />
              <Route path="/onboarding/commission-setup" element={<CommissionSetupScreen />} />
              <Route path="/onboarding/weekly-focus" element={<WeeklyFocusScreen />} />
              <Route path="/onboarding/commission-cap" element={<CommissionCapScreen />} />
              <Route path="/onboarding/branding-setup" element={<BrandingSetupScreen />} />
              <Route path="/onboarding/social-voice" element={<SocialVoiceScreen />} />
              <Route path="/onboarding/completion" element={<CompletionScreen />} />
              
              {/* Auth Routes */}
              <Route path="/auth/login/*" element={<LoginPage />} />
              <Route path="/auth/register/*" element={<RegisterPage />} />
              <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
              <Route path="/set-password" element={<SetPasswordPage />} />
              <Route path="/account" element={<MyAccountPage />} />
              
              {/* Legal Routes */}
              <Route path="/legal/terms" element={<TermsPage />} />
              <Route path="/legal/privacy" element={<PrivacyPage />} />
              <Route path="/legal/cookies" element={<CookiePolicyPage />} />
              <Route path="/legal/accessibility" element={<AccessibilityPage />} />
              
              {/* Admin Routes */}
              <Route path="/app/admin" element={<AdminConsolePage />} />
              </Routes>
            </OnboardingProvider>
          </AuthProvider>
          </ClerkProviderWithNavigation>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;